import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Lock } from "lucide-react";
import { SunMark } from "@/components/Logo";

const API = process.env.REACT_APP_BACKEND_URL;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function OwnerSetup() {
  const [params] = useSearchParams();
  const [token, setToken] = useState(params.get("token") || "");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next !== confirm) {
      setErr("The two passwords don't match.");
      return;
    }
    if (next.length < 10) {
      setErr("The password must be at least 10 characters.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/api/admin/owner-setup`, {
        token: token.trim(),
        newPassword: next,
      });
      setDone(true);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-line-strong rounded-md px-4 py-3 text-[15px] text-ink placeholder:text-mist/70 focus:border-gold focus:ring-1 focus:ring-gold outline-none transition-colors";

  return (
    <div className="min-h-[80vh] bg-ivory flex items-center justify-center px-5 py-24">
      <div className="w-full max-w-md">
        {done ? (
          <div
            className="bg-white border border-line rounded-md p-10 text-center"
            data-testid="owner-setup-success"
            role="status"
          >
            <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold-light">
              <CheckCircle2 className="w-8 h-8 text-gold-hover" />
            </span>
            <h1 className="mt-6 font-serif text-3xl text-navy">
              Your password is set.
            </h1>
            <p className="mt-4 text-stone2 leading-relaxed">
              It was stored only as an encrypted hash — nowhere as readable
              text. This setup link is now closed and cannot be used again.
            </p>
            <Link
              to="/admin"
              data-testid="owner-setup-signin-link"
              className="mt-8 inline-flex items-center justify-center bg-navy text-ivory px-7 py-3.5 rounded-full text-sm font-medium hover:bg-navy-light transition-colors"
            >
              Sign in to the review area
            </Link>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="bg-white border border-line rounded-md p-9"
          >
            <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-navy">
              <Lock className="w-5 h-5 text-gold" />
            </span>
            <div className="flex items-center gap-2 mt-6">
              <SunMark className="w-5 h-5 text-gold" />
              <h1 className="font-serif text-2xl text-navy">Owner setup</h1>
            </div>
            <p className="mt-2 text-sm text-stone2 leading-relaxed">
              Choose the password for your private review area. This setup link
              works once, for the site owner only.
            </p>
            <div className="mt-7 space-y-5">
              <div>
                <label htmlFor="os-token" className="block text-sm font-medium text-navy">
                  Setup code
                </label>
                <input
                  id="os-token"
                  type="text"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  data-testid="owner-setup-token-input"
                  className={`${inputCls} mt-2`}
                  placeholder="Paste the code from your setup link"
                  required
                />
              </div>
              <div>
                <label htmlFor="os-pass" className="block text-sm font-medium text-navy">
                  New password
                </label>
                <input
                  id="os-pass"
                  type="password"
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  data-testid="owner-setup-password"
                  autoComplete="new-password"
                  className={`${inputCls} mt-2`}
                  placeholder="At least 10 characters"
                  required
                />
              </div>
              <div>
                <label htmlFor="os-confirm" className="block text-sm font-medium text-navy">
                  Repeat it
                </label>
                <input
                  id="os-confirm"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="owner-setup-confirm"
                  autoComplete="new-password"
                  className={`${inputCls} mt-2`}
                  required
                />
              </div>
              {err && (
                <p className="text-sm text-burgundy" role="alert" data-testid="owner-setup-error">
                  {err}
                </p>
              )}
              <button
                type="submit"
                disabled={busy}
                data-testid="owner-setup-submit"
                className="w-full bg-navy text-ivory py-3.5 rounded-full text-sm font-medium hover:bg-navy-light transition-colors disabled:opacity-60 disabled:cursor-wait"
              >
                {busy ? "Setting…" : "Set my password"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
